'use client';

import { VoteType } from '@prisma/client';
import { AnimatePresence } from 'framer-motion';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { InContentBanner } from '../../../components/banner/InContentBanner';
import { SuggestionSeed } from '../../../types/shared';
import { voteOnSuggestion } from '../lib/actions/suggestionActions';
import { ItemViewModal } from './shared/ItemViewModal';
import { SuggestionCardLive } from './SuggestionCardLive';

function applyOptimisticVote(suggestion: SuggestionSeed, voteType: VoteType): SuggestionSeed {
  const prevVote = suggestion.myVote ?? null;
  let likes = suggestion.initialLikes ?? 0;
  let dislikes = suggestion.initialDislikes ?? 0;

  if (prevVote === voteType) {
    if (voteType === VoteType.LIKE) likes = Math.max(0, likes - 1);
    if (voteType === VoteType.DISLIKE) dislikes = Math.max(0, dislikes - 1);
    return { ...suggestion, myVote: null, initialLikes: likes, initialDislikes: dislikes };
  }

  if (prevVote === VoteType.LIKE) likes = Math.max(0, likes - 1);
  if (prevVote === VoteType.DISLIKE) dislikes = Math.max(0, dislikes - 1);

  if (voteType === VoteType.LIKE) likes += 1;
  if (voteType === VoteType.DISLIKE) dislikes += 1;

  return { ...suggestion, myVote: voteType, initialLikes: likes, initialDislikes: dislikes };
}

export function SuggestionGallery({
  suggestions,
  initialActiveItem,
}: {
  suggestions: SuggestionSeed[] | null;
  initialActiveItem: SuggestionSeed | null;
}) {
  const [activeItem, setActiveItem] = useState<SuggestionSeed | null>(initialActiveItem);

  // локальный список только для смены фильтров/страниц
  const [items, setItems] = useState<SuggestionSeed[]>(suggestions || []);

  const { data: session } = useSession();
  const userId = session?.user?.id;

  const searchParams = useSearchParams();
  const commentIdToScroll = searchParams.get('commentId');

  useEffect(() => {
    setItems(suggestions || []);
  }, [suggestions]);

  useEffect(() => {
    if (!activeItem) return;
    const fresh = items.find((item) => item.id === activeItem.id);
    if (fresh) setActiveItem(fresh);
  }, [items, activeItem?.id]);

  const applyOptimisticVoteToState = useCallback((suggestionId: string, voteType: VoteType) => {
    setItems((current) =>
      current.map((item) =>
        item.id === suggestionId ? applyOptimisticVote(item, voteType) : item,
      ),
    );
    setActiveItem((current) =>
      current?.id === suggestionId ? applyOptimisticVote(current, voteType) : current,
    );
  }, []);

  const handleVote = useCallback(
    async (suggestionId: string, voteType: VoteType) => {
      if (!userId) {
        toast.error('Войдите, чтобы голосовать');
        return;
      }

      applyOptimisticVoteToState(suggestionId, voteType);

      try {
        await voteOnSuggestion(suggestionId, voteType);
      } catch {
        toast.error('Не удалось сохранить голос');
      }
    },
    [userId, applyOptimisticVoteToState],
  );

  const itemsWithBanner = useMemo(() => {
    const arr: (SuggestionSeed | { type: 'banner' })[] = [...items];
    if (arr.length > 3) arr.splice(3, 0, { type: 'banner' });
    return arr;
  }, [items]);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 mt-6">
        {itemsWithBanner.map((item) => {
          if ('type' in item && item.type === 'banner') {
            return (
              <div key="banner" className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
                <InContentBanner />
              </div>
            );
          }

          const suggestion = item as SuggestionSeed;

          return (
            <SuggestionCardLive
              key={suggestion.id}
              suggestion={suggestion}
              myVote={suggestion.myVote ?? null}
              onCardClick={() => setActiveItem(suggestion)}
              onVote={(voteType) => handleVote(suggestion.id, voteType)}
            />
          );
        })}
      </div>

      <AnimatePresence>
        {activeItem && (
          <ItemViewModal
            key={activeItem.id}
            itemSeed={{
              id: activeItem.id,
              title: activeItem.title,
              description: activeItem.description,
              imageUrl: activeItem.imageUrl,
              initialLikes: activeItem.initialLikes,
              initialDislikes: activeItem.initialDislikes,
              commentCount: activeItem.commentCount,
              myVote: activeItem.myVote,
            }}
            itemType="suggestion"
            onClose={() => setActiveItem(null)}
            scrollToCommentId={commentIdToScroll}
            onVoteOptimistic={applyOptimisticVoteToState}
          />
        )}
      </AnimatePresence>
    </>
  );
}
